/**
 * Active session state.
 *
 * The JWT is stored in localStorage under `smt_session_v1`. We intentionally
 * use localStorage (not sessionStorage) so a page refresh keeps the user
 * signed in — the mockup behaved the same way and customers expect it.
 *
 * A future M7 change will move the token to an HttpOnly cookie. When that
 * happens, only this file needs to change: the rest of the app reads/writes
 * session through these helpers.
 */
import type { User } from '../types/api.ts';

const STORAGE_KEY = 'smt_session_v1';

export interface Session {
  token: string;
  expiresAt: number; // unix seconds
  user: User;
}

let cached: Session | null | undefined; // undefined = "not yet read"

function readFromStorage(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed.token || !parsed.user) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  if (cached === undefined) cached = readFromStorage();
  return cached;
}

export function setSession(session: Session): void {
  cached = session;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage may be unavailable (private mode); the in-memory cache still
    // covers the current page load, so swallow.
  }
}

export function clearSession(): void {
  cached = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see setSession */
  }
}

export function isExpired(s: Session | null): boolean {
  if (!s) return true;
  return Math.floor(Date.now() / 1000) >= s.expiresAt;
}

export function currentUser(): User | null {
  const s = getSession();
  return s && !isExpired(s) ? s.user : null;
}

// Level=1 accounts are region-scoped; without any allowed_regions the
// backend 403s the listing ("no allowed_regions configured"), so the FE
// routes these users straight to "Omat tilat" (asiakaspalaute 12.6.2026).
export function isRegionScopedWithoutRegions(u: User | null): boolean {
  return !!u && u.role !== 'admin' && u.level === 1 && (u.allowed_regions?.length ?? 0) === 0;
}

export function bearerHeader(): Record<string, string> {
  const s = getSession();
  return s && !isExpired(s) ? { Authorization: `Bearer ${s.token}` } : {};
}
